import { FC, useContext, useState } from "react";
import classNames from "../../utils/twClassNames";
import DistanceSelectSlider from "../distanceSelectSlider";
import FilterMenuSearchExtended from "../filterMenuSearchExtended";
import GenderToggles from "../genderToggles";

import useAuth from "../../hooks/useAuth";
import useSearch from "../../hooks/useSearch";
import StyledSelectSlider from "../styledSelectSlider";
import AdvancedSearchLink from "../advancedSearchLink";
import StartSearchButton from "../startSearchButton";
import { TranslationContext } from "../../context/TranslationContext";

import LoadingSpinner from "../loadingSpinner";

import api from "../../backendAPI";

import { tap, delay } from "rxjs/operators";
import { from } from "rxjs";

// todo (simon) -> abstract more general component from FilterMenu and FilterMenuSearch

const FilterMenuSearch: FC = () => {
  const { i } = useContext(TranslationContext);
  const {
    selectedMinAge,
    setSelectedMinAge,
    selectedGenderName,
    setSelectedGenderName,
    distance,
    setDistance,
    selectedMaxAge,
    setSelectedMaxAge,
    fetchResults,
    location,
    selectedPlaceName,
    resetFilters,
    shouldShowSlider,
    nbAdvancedFilters,
  } = useSearch();

  const { getLoginInfo } = useAuth();

  const [moreFiltersEnabled, setMoreFiltersEnabled] = useState(false);

  const [isReseting, setIsReseting] = useState(false);

  const [advancedSearchEnabled, setAdvancedSearchEnabled] = useState(false);

  const onSearchClick = (shouldReload: boolean) => {
    setMoreFiltersEnabled(false);
    setAdvancedSearchEnabled(false);
    fetchResults(shouldReload);
  };

  const onResetClick = () => {
    from(api.search.resetSearch())
      .pipe(
        tap(() => {
          setIsReseting(true);
          window.scrollTo({ top: 0, behavior: "smooth" });

          setMoreFiltersEnabled(false);
          setAdvancedSearchEnabled(false);
        }),
        tap(() => getLoginInfo()),

        tap(() => {
          resetFilters();
        }),
        delay(750),

        tap(() => {
          fetchResults(true);
        }),

        tap(() => {
          setIsReseting(false);
        })
      )
      .subscribe();
  };

  return (
    <div className="FilterMenuSearch bg-custom-gray/20 rounded-lg no-ads">
      <div
        className={classNames(
          moreFiltersEnabled ? "md:bg-custom-gray/20 rounded-lg" : "",
          "flex flex-col md:flex-row w-full justify-between md:items-center md:space-y-0 space-y-6 md:px-6 pt-4 px-5 pb-16 md:pb-10"
        )}
      >
        <div className="md:w-1/2 w-full relative">
          <StyledSelectSlider
            displayTitleWidth={45}
            label={i("Age")}
            range={{ min: 18, max: 100 }}
            selectedRange={{ min: selectedMinAge, max: selectedMaxAge }}
            setSelectedRange={(range) => {
              setSelectedMinAge(range.min);
              setSelectedMaxAge(range.max);
            }}
          />
          <div className="absolute -bottom-8 left-0 hidden md:block">
            <AdvancedSearchLink
              text={
                (moreFiltersEnabled ? i("Hide filters") : i("Show filters")) +
                (nbAdvancedFilters > 0
                  ? ` (${nbAdvancedFilters}
                  ${i(
                    (nbAdvancedFilters > 1 ? "filters" : "filter") + " applied"
                  )})`
                  : "")
              }
              onClick={() => setMoreFiltersEnabled((prevState) => !prevState)}
            />
          </div>
        </div>
        <div className="hidden md:min-w-[48px] md:inline" />
        <div className="md:w-1/2 w-full">
          <DistanceSelectSlider
            distance={distance}
            setDistance={setDistance}
            location={selectedPlaceName || (location ? location.name : "")}
            withHelper={true}
            maxDistance={500}
            shouldShowSlider={shouldShowSlider}
          />
        </div>
        <div className="hidden md:min-w-[24px] md:inline" />
        <div className="md:min-w-[210px] md:w-[210px] w-full">
          <GenderToggles
            hasSmallSpacing
            genders={genders}
            selectedGenderName={selectedGenderName}
            setSelectedGenderName={setSelectedGenderName}
          />
        </div>
        <div className="hidden md:min-w-[24px] md:inline" />
        <div className="h-[45px] md:w-[45px] w-full md:inline">
          <div className="StartSearchButtonContainer mt-4">
            <StartSearchButton fetchResults={onSearchClick} />

            <div className="block md:hidden mt-3">
              <AdvancedSearchLink
                text={
                  (moreFiltersEnabled ? i("Hide filters") : i("Show filters")) +
                  (nbAdvancedFilters > 0
                    ? ` (${nbAdvancedFilters}
                  ${i(
                    (nbAdvancedFilters > 1 ? "filters" : "filter") + " applied"
                  )})`
                    : "")
                }
                onClick={() => setMoreFiltersEnabled((prevState) => !prevState)}
              />
            </div>
          </div>
        </div>
      </div>

      {isReseting && (
        <div>
          <div className="w-full h-[670px] flex justify-center items-center">
            <LoadingSpinner />
          </div>
        </div>
      )}

      {!isReseting && moreFiltersEnabled && (
        <FilterMenuSearchExtended
          advancedSearchEnabled={advancedSearchEnabled}
          setAdvancedSearchEnabled={setAdvancedSearchEnabled}
          setMoreFiltersEnabled={setMoreFiltersEnabled}
          onResetClick={onResetClick}
        />
      )}
    </div>
  );
};

export default FilterMenuSearch;

export type GenderName = "Woman" | "Man" | "Both";

export type Gender = { name: GenderName; icon: string };

export const genders: Gender[] = [
  { name: "Woman", icon: "femaleSignWhite" },
  { name: "Man", icon: "manSignWhite" },
  { name: "Both", icon: "nonBinarySignWhite" },
];
